import { useState } from "react";
import { ArrowLeft, GraduationCap, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/auth.store";

/**
 * OnboardingBanner - Shows a banner on allowed pages during onboarding
 * Allows users to return to the onboarding wizard
 *
 * Usage:
 * Add to pages like EnvelopeCreate, Templates, etc:
 * ```jsx
 * import { OnboardingBanner } from "@/pages/onboarding/components";
 *
 * function EnvelopeCreate() {
 *   return (
 *     <div>
 *       <OnboardingBanner />
 *       {/* rest of page *\/}
 *     </div>
 *   );
 * }
 * ```
 */
export const OnboardingBanner = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show if onboarding is complete or dismissed
  if (user?.hasCompletedOnboarding || isDismissed) {
    return null;
  }

  return (
    <div
      className={`bg-primary/10 border border-primary/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 ${className || ""}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex w-10 h-10 rounded-full bg-primary/20 items-center justify-center shrink-0">
            <GraduationCap className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm sm:text-base text-foreground">
              {t(
                "onboarding.banner.title",
                "You're in the middle of the setup wizard",
              )}
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              {t(
                "onboarding.banner.description",
                "Feel free to explore, then return to complete your setup.",
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate("/onboarding")}
            className="gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">
              {t("onboarding.banner.return", "Return to Setup")}
            </span>
            <span className="sm:hidden">
              {t("onboarding.banner.returnShort", "Setup")}
            </span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsDismissed(true)}
            className="px-2"
            aria-label={t("common.dismiss", "Dismiss")}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * OnboardingFloatingButton - A floating button alternative to the banner
 * Shows in the corner of the screen during onboarding
 */
export const OnboardingFloatingButton = ({ position = "bottom-right" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  // Don't show if onboarding is complete
  if (user?.hasCompletedOnboarding) {
    return null;
  }

  const positionClasses = {
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "top-right": "top-20 right-4",
    "top-left": "top-20 left-4",
  };

  return (
    <Button
      onClick={() => navigate("/onboarding")}
      className={`fixed ${positionClasses[position]} z-40 gap-2 shadow-lg`}
    >
      <ArrowLeft className="w-4 h-4" />
      {t("onboarding.banner.return", "Return to Setup")}
    </Button>
  );
};

export default OnboardingBanner;
